import React, { useState } from 'react'
import {
  FolderOpen,
  FileText,
  Image as ImageIcon,
  Download,
  Trash2,
  Star,
  AlertCircle,
  Eye,
  ExternalLink,
  X,
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { useContentLibrary } from '../hooks/useContentLibrary'

function formatFileSize(bytes?: number): string {
  if (!bytes) return 'N/A'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 24) return 'Today'
  if (diffInHours < 48) return 'Yesterday'

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'text':
      return React.createElement(FileText, { className: 'h-4 w-4 mr-3 text-blue-500' })
    case 'image':
      return React.createElement(ImageIcon, { className: 'h-4 w-4 mr-3 text-purple-500' })
    default:
      return React.createElement(FileText, { className: 'h-4 w-4 mr-3 text-slate-500' })
  }
}

export function ContentLibrary() {
  const { content, loading, error, deleteContent, toggleFavorite } = useContentLibrary()
  const [previewItem, setPreviewItem] = useState<any | null>(null)

  const handleDelete = async (id: string, name: string) => {
    if (confirm('Are you sure you want to delete "' + name + '"?')) {
      await deleteContent(id)
    }
  }

  const handlePreview = (item: any) => {
    if (item.type === 'image' || item.type === 'text') {
      setPreviewItem(item)
    } else if (item.file_url) {
      // For other types, open in new tab
      window.open(item.file_url, '_blank')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-500 mt-4">Loading your content...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 mb-1">Failed to load content</h3>
              <p className="text-sm text-red-600">{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Content Library</h1>
          <p className="text-sm text-slate-600 mt-1">
            {content.length} {content.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>
      </div>

      {content.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              No content yet
            </h3>
            <p className="text-slate-600 mb-6 max-w-sm mx-auto">
              When you save documents from tools like OCR, they will appear here for easy access.
            </p>
          </div>
        </Card>
      ) : (
        <Card noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">Tool</th>
                  <th className="px-6 py-4">Date Modified</th>
                  <th className="px-6 py-4">Size</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {content.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      <div className="flex items-center">
                        {getTypeIcon(item.type)}
                        {item.file_url ? (
                          <a
                            href={item.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate max-w-xs text-blue-600 hover:text-blue-700 hover:underline cursor-pointer flex items-center gap-1"
                            title="Open file"
                          >
                            {item.name}
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="truncate max-w-xs">{item.name}</span>
                        )}
                        {item.is_favorite && (
                          <Star className="h-4 w-4 ml-2 text-amber-500 fill-amber-500 flex-shrink-0" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500 capitalize">
                      {item.type}
                    </td>
                    <td className="px-6 py-4 text-slate-500 uppercase text-xs font-medium">
                      {item.tool_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {formatDate(item.updated_at || item.created_at)}
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {formatFileSize(item.file_size)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleFavorite(item.id)}
                          className="text-slate-400 hover:text-amber-500 transition-colors p-1"
                          title={item.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star className={'h-4 w-4 ' + (item.is_favorite ? 'fill-amber-500 text-amber-500' : '')} />
                        </button>
                        {item.file_url && (
                          <>
                            <button
                              onClick={() => handlePreview(item)}
                              className="text-slate-400 hover:text-purple-600 transition-colors p-1"
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <a
                              href={item.file_url}
                              target={item.type === 'image' ? '_blank' : undefined}
                              download={item.type !== 'image' ? true : undefined}
                              rel={item.type === 'image' ? 'noopener noreferrer' : undefined}
                              className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                              title={item.type === 'image' ? 'Open in new tab' : 'Download'}
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(item.id, item.name)}
                          className="text-slate-400 hover:text-red-600 transition-colors p-1"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {getTypeIcon(previewItem.type)}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{previewItem.name}</h3>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(previewItem.file_size)} · {formatDate(previewItem.created_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setPreviewItem(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {previewItem.type === 'image' && previewItem.file_url && (
                <img
                  src={previewItem.file_url}
                  alt={previewItem.name}
                  className="max-w-full h-auto rounded-lg"
                />
              )}

              {previewItem.type === 'text' && previewItem.metadata?.content && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">
                    {previewItem.metadata.content}
                  </pre>
                </div>
              )}

              {previewItem.file_url && (
                <div className="mt-4 flex gap-2">
                  <a
                    href={previewItem.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in New Tab
                  </a>
                  <a
                    href={previewItem.file_url}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
